import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PencilSimple, Plus, Stack, Trash } from '@phosphor-icons/react';

import { DashboardLayout } from '../components/DashboardLayout';
import { API, formatApiErrorDetail } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

export function TemplateLibraryPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/templates`, { withCredentials: true });
      setTemplates(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Templates konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filteredTemplates = useMemo(() => templates.filter((template) => {
    const query = search.trim().toLowerCase();
    const matchesQuery = !query
      || template.name?.toLowerCase().includes(query)
      || template.description?.toLowerCase().includes(query);
    const matchesScope = scopeFilter === 'all'
      || (scopeFilter === 'base' && template.is_base_template)
      || (scopeFilter === 'department' && !template.is_base_template);
    return matchesQuery && matchesScope;
  }), [search, scopeFilter, templates]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/templates/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Template wurde gelöscht.');
      setDeleteTarget(null);
      fetchTemplates();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Löschen fehlgeschlagen.');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="template-library-page">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Template Designer</p>
            <h1 className="text-4xl font-black tracking-tight text-zinc-950">Templates</h1>
            <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
              Vorhandene Ticket- und Zertifikatsvorlagen laden, weiterentwickeln und neue Layouts im Designer anlegen.
            </p>
          </div>
          <Button
            className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90"
            onClick={() => navigate('/templates/new')}
            data-testid="template-library-create-button"
          >
            <Plus className="w-4 h-4" />
            Neues Template
          </Button>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Templates durchsuchen..."
            className="border-zinc-300"
            data-testid="template-library-search-input"
          />
          <Select value={scopeFilter} onValueChange={setScopeFilter}>
            <SelectTrigger data-testid="template-library-scope-filter">
              <SelectValue placeholder="Alle Templates" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Templates</SelectItem>
              <SelectItem value="base">Basistemplates</SelectItem>
              <SelectItem value="department">Abteilungstemplates</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {error ? (
          <div className="border border-[#E63946] bg-white p-6 rounded-sm" data-testid="template-library-error-state">
            <p className="text-sm font-medium text-[#E63946]">{error}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={fetchTemplates}
              data-testid="template-library-retry-button"
            >
              Erneut laden
            </Button>
          </div>
        ) : loading ? (
          <div className="space-y-3" data-testid="template-library-loading-state">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="h-16 animate-pulse rounded-sm border border-zinc-200 bg-zinc-100" />
            ))}
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="border border-zinc-200 bg-white p-10 rounded-sm text-center" data-testid="template-library-empty-state">
            <Stack className="w-12 h-12 mx-auto text-zinc-300" />
            <h2 className="mt-4 text-lg font-bold text-zinc-950">Noch keine Templates vorhanden</h2>
            <p className="mt-2 text-sm text-zinc-600">Lege das erste Template direkt im Designer an.</p>
            <Button
              className="mt-6 bg-[#002FA7] text-white hover:bg-[#002FA7]/90"
              onClick={() => navigate('/templates/new')}
              data-testid="template-library-empty-create-button"
            >
              <Plus className="w-4 h-4" />
              Template anlegen
            </Button>
          </div>
        ) : (
          <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid="template-library-table">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Scope</th>
                    <th className="px-4 py-3">Version</th>
                    <th className="px-4 py-3">Größe</th>
                    <th className="px-4 py-3">Elemente</th>
                    <th className="px-4 py-3">Aktualisiert</th>
                    <th className="px-4 py-3 text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplates.map((template) => (
                    <tr key={template.id} className="border-b border-zinc-100 last:border-b-0" data-testid={`template-row-${template.id}`}>
                      <td className="px-4 py-4 align-top">
                        <div>
                          <p className="font-semibold text-zinc-950">{template.name}</p>
                          <p className="mt-1 text-sm text-zinc-500">{template.description || 'Ohne Beschreibung'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-zinc-700">
                        {template.is_base_template ? 'Basis' : (template.department_id ? 'Abteilung' : 'Global')}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-zinc-700">
                        v{template.version || 1} · Schema {template.schema_version || 1}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-zinc-700">
                        {template.width} × {template.height}px
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-zinc-700">
                        {template.elements?.length || template.placeholders?.length || 0}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-zinc-700">
                        {new Date(template.updated_at || template.created_at).toLocaleString('de-DE')}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/templates/${template.id}/edit`)}
                            data-testid={`template-edit-button-${template.id}`}
                          >
                            <PencilSimple className="w-4 h-4" />
                            Bearbeiten
                          </Button>
                          <Button
                            variant="outline"
                            className="text-[#E63946] hover:text-[#E63946]"
                            onClick={() => setDeleteTarget(template)}
                            data-testid={`template-delete-button-${template.id}`}
                          >
                            <Trash className="w-4 h-4" />
                            Löschen
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm border-2 border-zinc-950 shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="template-delete-dialog-title">Template löschen?</AlertDialogTitle>
            <AlertDialogDescription data-testid="template-delete-dialog-description">
              {deleteTarget ? `"${deleteTarget.name}" wird deaktiviert. Dieser Schritt kann Auswirkungen auf verknüpfte Prozesse haben.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="template-delete-cancel-button">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#E63946] text-white hover:bg-[#E63946]/90"
              data-testid="template-delete-confirm-button"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
